/**
 * Fetch utility with automatic retry logic and exponential backoff
 */

export interface FetchWithRetryOptions {
  readonly maxRetries?: number;
  readonly delays?: readonly number[];
}

/**
 * Fetch with retry logic and exponential backoff for handling transient failures
 * @param url - URL to fetch
 * @param options - Fetch options
 * @param operationName - Name for logging purposes
 * @param retryOptions - Retry configuration
 * @returns Promise resolving to parsed JSON response
 * @throws Error if all retry attempts fail
 */
export async function fetchWithRetry<T>(
  url: string,
  options: RequestInit,
  operationName: string,
  retryOptions: FetchWithRetryOptions = {},
): Promise<T> {
  const { maxRetries = 3, delays = [0, 200, 800] } = retryOptions;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`${operationName} - Attempt ${attempt}/${maxRetries}`);

      const response = await fetch(url, options);

      if (response.status >= 400 && response.status < 500) {
        const errorData = (await response.json().catch(() => ({}))) as Record<
          string,
          unknown
        >;
        console.warn(
          `${operationName} - Attempt ${attempt} failed with ${response.status}:`,
          errorData,
        );

        if (attempt < maxRetries) {
          const delay = delays[attempt] ?? 800;
          console.log(`${operationName} - Waiting ${delay}ms before retry...`);
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }

        const message = (errorData.message ??
          errorData.description ??
          "Request failed") as string;
        throw new Error(`HTTP ${response.status}: ${message}`);
      }

      const data = (await response.json()) as T;
      console.log(`${operationName} - Success on attempt ${attempt}`);
      return data;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);

      if (attempt < maxRetries && errorMessage.includes("fetch")) {
        console.warn(
          `${operationName} - Network error on attempt ${attempt}, retrying...`,
        );
        const delay = delays[attempt] ?? 800;
        await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      }

      throw error;
    }
  }

  throw new Error(`${operationName} exceeded retry attempts`);
}
